// DS2.9 but with redux bindings
import React, { useEffect } from 'react';
import { useUniqueId } from '@wix/da-hooks/pkg/useUniqueId';
import UploadItemButtonDS from '@wix/da-ds/pkg/UploadItemButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import { useTranslation } from 'react-i18next';
import { FileUploadOptions, useFileUpload } from '../utils/hooks/useFileUpload';
import {
  UploadFailureCallback,
  UploadSuccessCallback,
} from '../redux/uploads/types';

import s from './UploadItemButton.scss';

export interface Props {
  uploadId?: string;
  className?: string;
  dropZoneClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  onSuccess?: UploadSuccessCallback;
  onFailure?: UploadFailureCallback;
  title?: string;
  subtitle?: string;
  uploadOptions?: FileUploadOptions;
  customIcon?: React.ReactNode;
  disableIcon?: boolean;
  ctaLabel?: string;
  aboveCtaSlot?: React.ReactNode;
  belowCtaSlot?: React.ReactNode;
  onDragChange?: (isDragging: boolean) => void;
}

const UploadItemButton: React.FC<Props> = ({
  uploadId,
  className,
  dropZoneClassName,
  contentClassName,
  titleClassName,
  subtitleClassName,
  onSuccess,
  onFailure,
  title,
  subtitle,
  uploadOptions,
  customIcon,
  disableIcon,
  ctaLabel,
  aboveCtaSlot,
  belowCtaSlot,
  onDragChange,
}) => {
  const { t } = useTranslation();
  const uniqueId = useUniqueId();
  const { input, getDropzoneRootProps, isDragging, activeUploads } =
    useFileUpload({
      id: uploadId || uniqueId,
      onSuccess,
      onFailure,
      ...uploadOptions,
    });
  useEffect(() => {
    onDragChange && onDragChange(isDragging);
  }, [onDragChange, isDragging]);

  const iconType = customIcon ? 'custom' : disableIcon ? false : 'llama';

  const activeUpload = [...activeUploads].pop();
  const errorMessage = activeUpload?.error?.cause
    ? (
      <>
        {t('somethingWentWrong')}.{' '}
        <SimpleTooltip
          usePortal
          className={s['error-tooltip']}
          text={`${activeUpload?.error?.cause}`}
        >
          {t('common.learnMore')}
        </SimpleTooltip>
      </>
    )
    : activeUpload?.error?.message;

  return (
    <UploadItemButtonDS
      className={className}
      dropZoneClassName={dropZoneClassName}
      contentClassName={contentClassName}
      titleClassName={titleClassName}
      subtitleClassName={subtitleClassName}
      iconType={iconType}
      icon={customIcon}
      rootProps={getDropzoneRootProps()}
      title={title}
      subtitle={subtitle}
      ctaLabel={ctaLabel}
      dropReadyTitle={t('uploadZone.drop.title')}
      progressTitle={t('uploadZone.progress.title')}
      fileInput={input}
      isDropReady={isDragging}
      progress={activeUpload?.progress}
      errorMessage={errorMessage}
      aboveCtaSlot={aboveCtaSlot}
      belowCtaSlot={belowCtaSlot}
    />
  );
};
UploadItemButton.displayName = 'UploadItemButton';

export default UploadItemButton;
